import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, PenLine, Calendar, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBlogs = async () => {
    try {
      const res = await axios.get(`${API}/blogs`);
      setBlogs(res.data);
    } catch (e) {
      console.error("Failed to fetch blogs", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBlogs(); }, []);

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    } catch { return dateStr; }
  };

  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4 animate-fade-up">Insurance Blog</h1>
          <p className="text-white/70 text-base lg:text-lg max-w-2xl mx-auto animate-fade-up delay-100">
            Stay informed with the latest insurance articles, tips, and insights from our experts.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section data-testid="blog-grid" className="bg-[#F8FAFC] py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-[#002147]" />
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-20">
              <PenLine className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-[#002147] mb-2">No Blog Posts Yet</h3>
              <p className="text-slate-500">Check back soon for insurance tips and insights from our experts.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <Link key={blog.id} to={`/blog/${blog.id}`}>
                  <Card
                    data-testid={`blog-card-${blog.id}`}
                    className="bg-white rounded-xl border border-slate-100 overflow-hidden card-hover h-full"
                  >
                    <CardContent className="p-0">
                      <div className="h-48 bg-gradient-to-br from-[#002147] to-[#0F3460] flex items-center justify-center">
                        <PenLine className="h-12 w-12 text-[#D4AF37]/30" />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge className="bg-[#D4AF37]/10 text-[#002147] border-0 text-xs font-medium">{blog.category}</Badge>
                          <span className="text-slate-400 text-xs flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(blog.created_at)}
                          </span>
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-[#002147] mb-2 line-clamp-2">{blog.title}</h3>
                        <p className="text-slate-500 text-sm leading-relaxed line-clamp-3">{blog.excerpt}</p>
                        <div className="mt-4 flex items-center text-[#D4AF37] text-sm font-semibold">
                          Read More <ArrowRight className="h-3 w-3 ml-1" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
